import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';

import {
  BackdropForDialog,
  CloseButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SubmitButton,
  ZoneCreateStatusDots,
} from 'components';
import { ROUTES } from 'consts';
import { EZoneCreateStatus } from 'enums';
import { DecalForm } from 'forms';
import { IZone } from 'models';
import { ZoneCustomizationForm } from '../../forms/zones/decal/zone-customization.form';

interface DecalApproveDialogProps {
  visible?: boolean;
  setVisible?: (value: boolean) => void;
  title?: string;
  submitButtonLabel?: string;
  zone?: IZone;
  hideDots?: boolean;
  next?: () => void;
  zoneIdsToApprove?: number[];
}

export const DecalApproveDialog = ({
  visible = false,
  setVisible = () => {},
  title = 'Decal Proof',
  submitButtonLabel = 'Approve Decal',
  zone,
  hideDots = false,
  next: _next,
  zoneIdsToApprove = [],
}: DecalApproveDialogProps) => {
  if (!zone) {
    return null;
  }

  const formRef = React.useRef<any>();
  const theme = useTheme();
  const down_sm = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [loading_form, setLoadingForm] = useState<boolean>(false);
  const [loading_submit, setLoadingSubmit] = useState<boolean>(false);
  const [buildingCustomization, setBuildingCustomization] = useState<boolean>(false);

  const next = () => {
    if (_next) {
      _next();
      setVisible(false);
    } else {
      navigate(ROUTES.ZONES_ACTIVE);
      setVisible(false);
    }
  };

  const onClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
      setVisible(false);
      document.body.removeAttribute('style');
    }
  };

  const onSubmit = () => {
    formRef.current.onSubmit();
  };

  return (
    <Dialog
      onClose={onClose}
      open={visible}
      PaperProps={{ sx: { marginBottom: 12, ...(down_sm ? {} : { overflow: 'visible' }) } }}
    >
      <DialogTitle>
        <Typography variant="h5" component="span">
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box>
          <DecalForm
            ref={formRef}
            zoneId={zone.id}
            onInit={(loading) => setLoadingForm(loading)}
            onSubmitting={(loading) => setLoadingSubmit(loading)}
            zoneIdsToApprove={zoneIdsToApprove}
            next={next}
          />
        </Box>
      </DialogContent>

      {!buildingCustomization && (
        <Box sx={{ paddingX: down_sm ? 3 : 8, paddingBottom: 2 }}>
          <Button sx={{ paddingX: 2 }} startIcon={<AutoAwesome />} onClick={() => setBuildingCustomization(true)}>
            Need to customize this decal?
          </Button>
        </Box>
      )}

      {buildingCustomization && (
        <Box
          sx={{
            marginTop: 6,
            paddingX: down_sm ? 3 : 8,
            paddingBottom: 3,
            bgcolor: theme.palette.grey[100],
            position: 'relative',
          }}
        >
          <ZoneCustomizationForm zoneId={zone.id} next={() => setBuildingCustomization(false)} />
        </Box>
      )}

      <DialogActions sx={{ paddingTop: 6 }}>
        <SubmitButton loading={loading_submit} label={submitButtonLabel} onClick={onSubmit} />
      </DialogActions>

      <CloseButton onClick={() => onClose(null, 'anyway')} />

      {!hideDots && (
        <Box
          sx={{
            width: '100%',
            left: 0,
            display: 'flex',
            justifyContent: 'center',
            ...(down_sm ? { paddingBottom: 6 } : { position: 'absolute', bottom: -60 }),
          }}
        >
          <ZoneCreateStatusDots status={EZoneCreateStatus.APPROVE_DECAL} />
        </Box>
      )}

      <BackdropForDialog loading={loading_form} hideSpinner />
    </Dialog>
  );
};
